import { ADMIN_JOINED_FROM, COMPANY_SUBSCRIPTION_STATUS, formatDate } from "../../../constants";
import { buildExportFilename, fetchAllPaginated } from "../../../utils/paginatedExport";

// The three sets of companies the Companies page can show / export.
export const COMPANY_EXPORT_TABS = [
  { key: "active", label: "Active Companies", icon: "fa-building" },
  { key: "legacy", label: "Legacy Companies", icon: "fa-star" },
  { key: "deleted", label: "Deleted Companies", icon: "fa-trash" },
];

/**
 * Builds the list request for a company tab. Shared by the table and the
 * export so the two can never diverge on endpoint or params.
 */
export const buildCompanyListRequest = ({
  tab,
  page,
  limit,
  searchTerm = "",
  joinedFrom = "ALL",
}) => {
  let url = `${process.env.REACT_APP_base_url}api/masterAdmin/admins-list`;

  // Legacy companies come from a different endpoint.
  if (tab === "legacy") {
    url = `${process.env.REACT_APP_base_url}api/masterAdmin/legacy-companies`;
  }

  const requestData = {
    auth_code: process.env.REACT_APP_AUTH_CODE,
    status: tab === "legacy" || tab === "signupIssue" || tab === "active" ? "1" : "0",
    page: page.toString(),
    limit: limit.toString(),
  };

  // Website sign-ups whose card/Stripe step failed and are not yet resolved.
  if (tab === "signupIssue") {
    requestData.signupIssue = true;
  }

  if (searchTerm.trim()) {
    requestData.search = searchTerm.trim();
  }

  // The legacy endpoint ignores joinedFrom.
  if (joinedFrom !== "ALL" && tab !== "legacy") {
    requestData.joinedFrom = joinedFrom;
  }

  return { url, requestData };
};

/** Fetches companies for a tab by looping through the paginated endpoint. */
export const fetchAllCompanies = ({
  tab,
  searchTerm = "",
  joinedFrom = "ALL",
  fromPage,
  toPage,
  pageSize,
  onProgress,
  isCancelled,
}) =>
  fetchAllPaginated({
    buildRequest: (page, limit) =>
      buildCompanyListRequest({ tab, page, limit, searchTerm, joinedFrom }),
    fromPage,
    toPage,
    pageSize,
    onProgress,
    isCancelled,
  });

/** Describes the filters the export will apply, for the modal's summary. */
export const buildCompanyFilterSummary = ({ tab, searchTerm = "", joinedFrom = "ALL" }) => {
  const items = [];
  const notes = [];

  if (searchTerm.trim()) {
    items.push({ label: "Search", value: `"${searchTerm.trim()}"` });
  }

  if (joinedFrom !== "ALL") {
    if (tab === "legacy") {
      notes.push("The Joined From filter does not apply to legacy companies and will be ignored.");
    } else {
      items.push({ label: "Joined From", value: ADMIN_JOINED_FROM[joinedFrom] || joinedFrom });
    }
  }

  if (tab === "legacy") {
    notes.push(
      "The legacy companies endpoint does not return Paid User Count or Next Invoice Date, so those columns will show 0 / -."
    );
  }

  return { items, notes };
};

/**
 * CSV columns, mirroring the Companies table. Composite table cells
 * (Company Info, User Count, Add-On Usage) are split into their own columns so
 * the file is usable in a spreadsheet; the Action column is omitted.
 */
export const COMPANY_EXPORT_COLUMNS = [
  { header: "Sr. No.", value: (row, index) => index + 1 },
  { header: "Company Name", value: (row) => row.username },
  { header: "Email", value: (row) => row.email },
  { header: "Trial End Date", value: (row) => formatDate(row.trialPeriodEndDate) },
  {
    header: "Payment Mode",
    value: (row) => (row.validPaymentMethodRequired === true ? "Automatic" : "Manual"),
  },
  {
    header: "Cancelling",
    value: (row) => (row.validPaymentMethodRequired && row.cancelAtPeriodEnd ? "Yes" : "No"),
  },
  { header: "Legacy Status", value: (row) => (row.legacyCompany ? "Legacy" : "Standard") },
  { header: "Trial Mode", value: (row) => (row.trialModeEnabled === true ? "Active" : "Disabled") },
  {
    header: "Subscription Status",
    value: (row) =>
      row.validPaymentMethodRequired === true
        ? (COMPANY_SUBSCRIPTION_STATUS[row?.currentSubscription?.status] || "Unknown").toUpperCase()
        : "Manual Billing",
  },
  { header: "Next Invoice Date", value: (row) => formatDate(row.nextInvoiceDate) },
  {
    header: "Foundation Analysis Count",
    value: (row) => (row.addOnCounts ? row.addOnCounts.foundationAnalysisCount || 0 : "-"),
  },
  {
    header: "Floor Level Count",
    value: (row) => (row.addOnCounts ? row.addOnCounts.floorLevelCount || 0 : "-"),
  },
  { header: "Active User Count", value: (row) => row?.activeUserCount || 0 },
  { header: "Paid User Count", value: (row) => row?.paidUserCount || 0 },
  { header: "Status", value: (row) => (row.status === 1 ? "Active" : "Deleted") },
];

export const buildCompanyExportFilename = ({
  tab,
  searchTerm = "",
  joinedFrom = "ALL",
  scopeParts = [],
}) => {
  const parts = [tab];

  if (searchTerm.trim()) {
    parts.push("search", searchTerm);
  }

  if (joinedFrom !== "ALL" && tab !== "legacy") {
    parts.push(joinedFrom);
  }

  return buildExportFilename("companies", [...parts, ...scopeParts]);
};
